/* Poke-Geek V3 — Global product media carousel enhancement */
(() => {
  if (window.__pgV3ProductMediaCarouselReady) return;
  window.__pgV3ProductMediaCarouselReady = true;

  const gallerySelector = '.template-product.pg-theme-v2 media-gallery.media-gallery--carousel';
  const locale = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  const translations = {
    fr: {
      showMedia: (index) => `Afficher le média ${index}`,
      thumbnails: 'Miniatures du produit',
    },
    en: {
      showMedia: (index) => `Show media ${index}`,
      thumbnails: 'Product thumbnails',
    },
    es: {
      showMedia: (index) => `Mostrar contenido multimedia ${index}`,
      thumbnails: 'Miniaturas del producto',
    },
    de: {
      showMedia: (index) => `Medium ${index} anzeigen`,
      thumbnails: 'Produktminiaturen',
    },
    it: {
      showMedia: (index) => `Mostra contenuto multimediale ${index}`,
      thumbnails: 'Miniature del prodotto',
    },
    nl: {
      showMedia: (index) => `Media ${index} weergeven`,
      thumbnails: 'Productminiaturen',
    },
  };
  const copy = translations[locale] || translations.en;

  const visibleSlides = (slideshow) =>
    Array.from(slideshow.querySelectorAll('slideshow-slide')).filter((slide) => !slide.hidden);

  const getActiveIndex = (slideshow, slides) => {
    const ariaIndex = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
    if (ariaIndex >= 0) return ariaIndex;

    const current = Number(slideshow.current);
    if (Number.isFinite(current) && current >= 0 && current < slides.length) return current;

    return 0;
  };

  const mediaSignature = (slides) =>
    slides
      .map((slide, index) => `${slide.getAttribute('slide-id') || slide.id || index}:${slide.hidden ? '0' : '1'}`)
      .join('|');

  const getThumbnailSource = (sourceImage) => {
    const source = sourceImage.currentSrc || sourceImage.src || sourceImage.getAttribute('src') || '';
    if (!source) return '';

    try {
      const url = new URL(source, window.location.href);
      if (url.hostname.includes('shopify')) {
        url.searchParams.set('width', '180');
      }
      return url.toString();
    } catch (_error) {
      return source;
    }
  };

  const keepThumbnailVisible = (thumbs, thumbnail) => {
    if (!thumbnail || thumbs.scrollWidth <= thumbs.clientWidth) return;

    const itemStart = thumbnail.offsetLeft;
    const itemEnd = itemStart + thumbnail.offsetWidth;
    const viewportStart = thumbs.scrollLeft;
    const viewportEnd = viewportStart + thumbs.clientWidth;

    if (itemStart >= viewportStart && itemEnd <= viewportEnd) return;

    const target = Math.max(0, itemStart - (thumbs.clientWidth - thumbnail.offsetWidth) / 2);
    thumbs.scrollTo({ left: target, behavior: 'smooth' });
  };

  const createThumbnail = (slide, index, slideshow) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pg-v3-media-carousel__thumb';
    button.setAttribute('aria-label', copy.showMedia(index + 1));

    const sourceImage = slide.querySelector('img');

    if (sourceImage) {
      const image = document.createElement('img');
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.src = getThumbnailSource(sourceImage);
      button.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'pg-v3-media-carousel__thumb-placeholder';
      placeholder.textContent = String(index + 1);
      button.append(placeholder);
    }

    button.addEventListener('click', (event) => {
      if (typeof slideshow.select === 'function') {
        slideshow.select(index, event);
      }
    });

    return button;
  };

  const initializeGallery = (gallery) => {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.pgV3CarouselReady === 'true') return;

    const slideshow = gallery.querySelector('slideshow-component');
    const container = slideshow?.querySelector('slideshow-container');

    if (!slideshow || !container) return;

    gallery.dataset.pgV3CarouselReady = 'true';
    gallery.classList.add('pg-v3-media-carousel');

    const counter = document.createElement('div');
    counter.className = 'pg-v3-media-carousel__counter';
    counter.setAttribute('aria-live', 'polite');
    container.append(counter);

    const controls = document.createElement('div');
    controls.className = 'pg-v3-media-carousel__controls';

    const thumbs = document.createElement('div');
    thumbs.className = 'pg-v3-media-carousel__thumbs';
    thumbs.setAttribute('role', 'group');
    thumbs.setAttribute('aria-label', copy.thumbnails);

    controls.append(thumbs);
    slideshow.insertAdjacentElement('afterend', controls);

    let lastSignature = '';
    let lastActiveIndex = -1;

    const refresh = () => {
      const slides = visibleSlides(slideshow);
      const signature = mediaSignature(slides);

      gallery.dataset.pgMediaCount = String(slides.length);

      if (signature !== lastSignature) {
        lastSignature = signature;
        thumbs.replaceChildren();

        slides.forEach((slide, index) => {
          thumbs.append(createThumbnail(slide, index, slideshow));
        });
      }

      if (!slides.length) {
        counter.textContent = '';
        return;
      }

      const activeIndex = Math.min(getActiveIndex(slideshow, slides), slides.length - 1);
      counter.textContent = `${activeIndex + 1} / ${slides.length}`;

      Array.from(thumbs.children).forEach((thumbnail, index) => {
        thumbnail.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });

      if (activeIndex !== lastActiveIndex) {
        keepThumbnailVisible(thumbs, thumbs.children[activeIndex]);
        lastActiveIndex = activeIndex;
      }
    };

    const observer = new MutationObserver(() => requestAnimationFrame(refresh));
    observer.observe(slideshow, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'hidden', 'src', 'srcset'],
    });

    refresh();
    requestAnimationFrame(refresh);
  };

  const initialize = (scope = document) => {
    if (scope instanceof Element && scope.matches(gallerySelector)) {
      initializeGallery(scope);
    }

    scope.querySelectorAll?.(gallerySelector).forEach(initializeGallery);
  };

  const start = () => {
    if (window.customElements?.whenDefined) {
      customElements.whenDefined('slideshow-component').then(() => initialize());
    } else {
      initialize();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('load', () => initialize(), { once: true });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
